import { create } from 'zustand';
import ServiceDctf from '@/services/ServiceDctf';
import { notify } from '@/stores/useToast';
import { mensagemErro } from '@/utils/apiError';

type Any = any;

const TIPO_LABEL: Record<string, string> = { mensal: 'Mensal', '13_salario': '13º salário' };

// CON-5188: cada coluna ordenável da tabela mapeia para o alias que o backend aceita
// (dctf/search?sort=<alias>,<asc|desc> no body). Só as colunas com alias entram como
// sortable — `competencia` é composta (ano + mês) e resolvida no backend.
const SORT_ALIASES: Record<string, string> = {
  tipo: 'tipo',
  competencia: 'competencia',
  debitoTotal: 'debitoTotal',
  creditosTotal: 'creditosTotal',
  saldoAPagarTotal: 'saldoAPagarTotal',
};

const fmtMoeda = (v?: string | number) =>
  v == null
    ? 'R$ 0,00'
    : `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

interface State {
  cnpjCliente: string;
  ano: number;
  mes: number;
  lista: Any[];
  total: number;
  page: number;
  size: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  detalhe: Any | null;
  carregando: boolean;
  carregandoDetalhe: boolean;
  sincronizando: boolean;

  selecionarEmpregador: (cnpj: string) => Promise<void>;
  setCompetencia: (ano: number, mes: number) => void;
  carregar: () => Promise<void>;
  setPage: (page: number) => Promise<void>;
  setSize: (size: number) => Promise<void>;
  setSort: (by: string, order: 'asc' | 'desc') => Promise<void>;
  sincronizar: () => Promise<void>;
  abrirDetalhe: (id: string) => Promise<void>;
  fecharDetalhe: () => void;
}

// item da API -> linha da tabela mestre.
const normalizar = (d: Any) => ({
  id: d.id,
  tipo: TIPO_LABEL[d.tipo as string] ?? d.tipo ?? '',
  competencia: d.mesApuracao
    ? `${String(d.mesApuracao).padStart(2, '0')}/${d.anoApuracao}`
    : String(d.anoApuracao ?? ''),
  debitoTotal: fmtMoeda(d.debitoTotal),
  creditosTotal: fmtMoeda(d.creditosTotal),
  saldoAPagarTotal: fmtMoeda(d.saldoAPagarTotal),
  zerada: !!d.indZerada,
});

const hoje = new Date();

const ESTADO_INICIAL = {
  cnpjCliente: '',
  ano: hoje.getFullYear(),
  mes: hoje.getMonth() + 1,
  lista: [] as Any[],
  total: 0,
  page: 0,
  size: 50,
  sortBy: '',
  sortOrder: 'asc' as 'asc' | 'desc',
  detalhe: null as Any,
  carregando: false,
  carregandoDetalhe: false,
  sincronizando: false,
};

export const useDctfStore = create<State>((set, get) => ({
  ...ESTADO_INICIAL,

  async selecionarEmpregador(cnpj) {
    set({ cnpjCliente: cnpj, page: 0 });
    await get().carregar();
  },

  setCompetencia(ano, mes) {
    set({ ano, mes });
  },

  async carregar() {
    const { cnpjCliente, page, size, sortBy, sortOrder } = get();
    if (!cnpjCliente) {
      set({ lista: [], total: 0 });
      return;
    }
    set({ carregando: true });
    try {
      const alias = SORT_ALIASES[sortBy];
      const sort = alias ? `${alias},${sortOrder}` : undefined;
      const { data } = await ServiceDctf.search({
        cnpj: cnpjCliente,
        page,
        size,
        ...(sort ? { sort } : {}),
      });
      set({ lista: (data?.items ?? []).map(normalizar), total: data?.total ?? 0 });
    } catch {
      set({ lista: [], total: 0 });
      notify.error('Erro ao carregar as declarações DCTF.');
    } finally {
      set({ carregando: false });
    }
  },

  async setPage(page) {
    set({ page });
    await get().carregar();
  },

  async setSize(size) {
    set({ size, page: 0 });
    await get().carregar();
  },

  async setSort(by, order) {
    set({ sortBy: by, sortOrder: order, page: 0 });
    await get().carregar();
  },

  async sincronizar() {
    const { cnpjCliente, ano, mes } = get();
    if (!cnpjCliente) {
      notify.error('Selecione um empregador.');
      return;
    }
    set({ sincronizando: true });
    try {
      await ServiceDctf.sincronizar(cnpjCliente, ano, mes);
      notify.success('DCTF sincronizada.');
      await get().carregar();
    } catch (err) {
      notify.error(mensagemErro(err, 'Erro ao sincronizar a DCTF.'));
    } finally {
      set({ sincronizando: false });
    }
  },

  async abrirDetalhe(id) {
    set({ carregandoDetalhe: true, detalhe: { id } });
    try {
      const { data } = await ServiceDctf.buscarPorId(id);
      set({ detalhe: data });
    } catch {
      notify.error('Erro ao carregar os detalhes da declaração.');
      set({ detalhe: null });
    } finally {
      set({ carregandoDetalhe: false });
    }
  },

  fecharDetalhe() {
    set({ detalhe: null });
  },
}));
